import { useForm } from '@tanstack/react-form';
import { ArrowUp } from 'lucide-react';
import { useMemo } from 'react';
import z from 'zod';

import { Button } from '@doger/ui/components/ui/button';
import { Textarea } from '@doger/ui/components/ui/textarea';
import { $$toast } from '@doger/ui/components/ui/toast';
import { ChatConfig } from '@doger/core/modules/chat';

import { useTranslation } from '../../shared/i18n';
import styles from './chat-input.module.scss';

type ChatInputProps = {
  name: string;
  isLoading: boolean;
  onSubmit: (text: string) => void;
};

export const ChatInput = ({ name, isLoading, onSubmit }: ChatInputProps) => {
  const { t, i18n } = useTranslation('app');

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        notation: 'standard',
      }),
    [i18n.language]
  );

  const MessageSchema = z.object({
    message: z
      .string()
      .trim()
      .min(1, t('validation.message.required'))
      .max(128_000, t('validation.message.tooLong')),
  });

  const form = useForm({
    defaultValues: {
      message: '',
    },
    onSubmit: ({ value, formApi }) => {
      onSubmit(value.message.trim());
      formApi.reset();
    },
    validators: {
      onChange: MessageSchema,
      onMount: MessageSchema,
    },
  });

  return (
    <div className={styles.bottomInput}>
      <div className={styles.container}>
        <form
          name={name}
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className={styles.form}
        >
          <form.Field
            name='message'
            children={(field) => (
              <Textarea
                className={styles.input}
                name={field.name}
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  const value = e.target.value;

                  if (value.length > ChatConfig.MAX_MESSAGE_LENGTH) {
                    $$toast.warning(
                      t('validation.message.truncated', {
                        maxLength: formatter.format(
                          ChatConfig.MAX_MESSAGE_LENGTH
                        ),
                      })
                    );
                  }

                  field.handleChange(
                    value.slice(0, ChatConfig.MAX_MESSAGE_LENGTH)
                  );
                  // Auto-resize textarea based on content
                  const textarea = e.target;
                  textarea.style.height = 'auto';
                  const newHeight = Math.min(textarea.scrollHeight, 144); // max 6 rows (144px)
                  textarea.style.height = `${newHeight}px`;
                }}
                placeholder={t('conversation.input.placeholder')}
                autoFocus
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();

                    form.handleSubmit();
                  }
                }}
                onBlur={field.handleBlur}
              />
            )}
          />
          <form.Subscribe
            selector={(state) => state.canSubmit}
            children={(canSubmit) => (
              <Button
                variant='contained'
                palette='primary'
                type='submit'
                disabled={isLoading || !canSubmit}
                size='sm'
                className={styles.sendButton}
              >
                <ArrowUp />
              </Button>
            )}
          />
        </form>
      </div>
      <p className={styles.disclaimer}>{t('disclaimer.text')}</p>
    </div>
  );
};
